/**
 * <s-auto-scroll> — Continuously auto-scrolling, swipeable horizontal list.
 *
 * Wraps a horizontally scrollable track ([data-track], native overflow-x scrolling,
 * so touch swipe and trackpad scrolling work out of the box). On mount the track's
 * items are cloned once (aria-hidden + inert, analytics wrappers stripped) so the
 * loop is seamless. Auto-scroll pauses on hover, focus, touch/pointer drag and wheel,
 * resumes after a short idle delay, stops while off-screen or the tab is hidden,
 * and never runs when the visitor prefers reduced motion.
 *
 * Element dataset:
 *   data-speed       — pixels per second (default 40).
 *   data-resume-delay — ms of inactivity before resuming after interaction (default 2500).
 */
class SAutoScroll extends HTMLElement {
  #ac;
  #track;
  #raf = 0;
  #pos = 0;
  #last = 0;
  #loopWidth = 0;
  #hovered = false;
  #focused = false;
  #interactingUntil = 0;
  #visible = false;
  #observer;
  #resizeObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    const items = [...this.#track.children];
    if (items.length < 2) return;

    if (!this.#track.querySelector('[data-auto-scroll-clone]')) {
      for (const item of items) {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-auto-scroll-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        for (const viewEvent of clone.querySelectorAll('s-view-event')) {
          const wrapper = document.createElement('div');
          wrapper.className = viewEvent.className;
          wrapper.append(...viewEvent.childNodes);
          viewEvent.replaceWith(wrapper);
        }
        this.#track.append(clone);
      }
    }

    this.#measure();
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.#track);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion.matches) return;

    const hold = () => {
      this.#interactingUntil = Number.POSITIVE_INFINITY;
    };
    const release = () => {
      this.#interactingUntil = performance.now() + Number(this.dataset.resumeDelay || 2500);
    };

    this.#track.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#hovered = true;
    }, { signal });
    this.#track.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.#hovered = false;
    }, { signal });
    this.#track.addEventListener('touchstart', hold, { signal, passive: true });
    this.#track.addEventListener('touchend', release, { signal, passive: true });
    this.#track.addEventListener('touchcancel', release, { signal, passive: true });
    this.#track.addEventListener('pointerdown', hold, { signal });
    this.#track.addEventListener('pointerup', release, { signal });
    this.#track.addEventListener('wheel', release, { signal, passive: true });
    this.#track.addEventListener('focusin', () => { this.#focused = true; }, { signal });
    this.#track.addEventListener('focusout', () => { this.#focused = false; }, { signal });
    this.#track.addEventListener('scroll', () => this.#wrapManualScroll(), { signal, passive: true });
    document.addEventListener('visibilitychange', () => this.#toggle(), { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#toggle();
    });
    this.#observer.observe(this);
  }

  /** Pause auto-scroll for `ms` milliseconds (e.g. while a sibling control runs a smooth scroll). */
  hold(ms = 3000) {
    this.#interactingUntil = Math.max(this.#interactingUntil, performance.now() + ms);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  #measure() {
    const first = this.#track.firstElementChild;
    const firstClone = this.#track.querySelector('[data-auto-scroll-clone]');
    if (!first || !firstClone) return;
    this.#loopWidth = firstClone.offsetLeft - first.offsetLeft;
  }

  #isPaused() {
    return this.#hovered || this.#focused || performance.now() < this.#interactingUntil;
  }

  #wrapManualScroll() {
    if (!this.#isPaused() || this.#loopWidth <= 0) return;
    const left = this.#track.scrollLeft;
    if (left >= this.#loopWidth) this.#track.scrollLeft = left - this.#loopWidth;
  }

  #toggle() {
    const shouldRun = this.#visible && !document.hidden;
    if (shouldRun && !this.#raf) {
      this.#last = performance.now();
      this.#pos = this.#track.scrollLeft;
      this.#raf = requestAnimationFrame((t) => this.#tick(t));
    } else if (!shouldRun && this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }
  }

  #tick(now) {
    const delta = Math.min(now - this.#last, 100);
    this.#last = now;

    if (this.#isPaused() || this.#loopWidth <= 0) {
      this.#pos = this.#track.scrollLeft;
    } else {
      const speed = Number(this.dataset.speed || 40);
      this.#pos += (speed * delta) / 1000;
      if (this.#pos >= this.#loopWidth) this.#pos -= this.#loopWidth;
      this.#track.scrollLeft = this.#pos;
    }

    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }
}

if (!customElements.get('s-auto-scroll')) {
  customElements.define('s-auto-scroll', SAutoScroll);
}
